/**
 * Contrast maths for client-chosen brand colours.
 *
 * The design system is built on one rule that has to survive a client picking their own colours:
 * the primary call to action is a solid block of colour with a label that is genuinely readable on
 * it. Today that is navy on orange at 5.56:1. A client who picks pale yellow or a near-black
 * maroon would break a hard-coded foreground, so the foreground is computed here instead, and the
 * hover shade is derived and then *checked* rather than assumed.
 *
 * Everything in this module is total: bad input (a colour name, an empty string, `NaN`) produces a
 * sensible fallback rather than an exception, because these run in `computed()` bodies and style
 * bindings where a throw would blank the screen.
 *
 * Zero dependencies on purpose — the same maths runs in both Vue apps, the Pages Functions and the
 * processor Worker.
 */

/* ------------------------------------------------------------------------------------------------
 * Parsing
 * ---------------------------------------------------------------------------------------------- */

/** An sRGB colour as three 0–255 channels. */
export interface RgbColor {
  r: number;
  g: number;
  b: number;
}

/** A CSS hex colour in shorthand (`#rgb`) or full (`#rrggbb`) form, in any case. */
const HEX_COLOR = /^#(?:[0-9a-f]{3}|[0-9a-f]{6})$/iu;

/**
 * Reads a hex colour into its three channels. Accepts `#rgb` and `#rrggbb` in either case, with
 * surrounding whitespace tolerated, and returns `null` for anything else — a colour name, an
 * `rgb()` string, a four-digit hex, an empty string, a non-string. Never throws.
 */
export function parseHexColor(value: string): RgbColor | null {
  if (typeof value !== 'string') return null;
  const text = value.trim();
  if (!HEX_COLOR.test(text)) return null;
  const hex = text.slice(1);
  const full =
    hex.length === 3
      ? `${hex[0] ?? ''}${hex[0] ?? ''}${hex[1] ?? ''}${hex[1] ?? ''}${hex[2] ?? ''}${hex[2] ?? ''}`
      : hex;
  return {
    r: Number.parseInt(full.slice(0, 2), 16),
    g: Number.parseInt(full.slice(2, 4), 16),
    b: Number.parseInt(full.slice(4, 6), 16),
  };
}

/** Renders one 0–255 channel as two lower-case hex digits. */
function channelToHex(value: number): string {
  const clamped = Math.max(0, Math.min(255, Math.round(value)));
  return clamped.toString(16).padStart(2, '0');
}

/** Renders three channels as a lower-case `#rrggbb` string. */
function toHex({ r, g, b }: RgbColor): string {
  return `#${channelToHex(r)}${channelToHex(g)}${channelToHex(b)}`;
}

/* ------------------------------------------------------------------------------------------------
 * WCAG contrast
 * ---------------------------------------------------------------------------------------------- */

/** Linearizes one 0–255 sRGB channel, per WCAG 2.1. */
function linearize(channel: number): number {
  const c = channel / 255;
  return c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4;
}

/**
 * WCAG 2.1 sRGB relative luminance, `0` for black through `1` for white. A colour that cannot be
 * parsed is treated as black, which keeps {@link contrastRatio} inside its 1–21 range rather than
 * leaking `NaN` into a style binding.
 */
export function relativeLuminance(hex: string): number {
  const rgb = parseHexColor(hex);
  if (!rgb) return 0;
  return 0.2126 * linearize(rgb.r) + 0.7152 * linearize(rgb.g) + 0.0722 * linearize(rgb.b);
}

/**
 * The WCAG 2.1 contrast ratio between two colours, from `1` (identical) to `21` (black on white).
 * Order does not matter.
 */
export function contrastRatio(a: string, b: string): number {
  const la = relativeLuminance(a);
  const lb = relativeLuminance(b);
  const lighter = Math.max(la, lb);
  const darker = Math.min(la, lb);
  return (lighter + 0.05) / (darker + 0.05);
}

/** The WCAG conformance levels this project checks against. */
export type ContrastLevel = 'AA' | 'AA-large' | 'AAA';

/** Minimum ratio for each level: normal text, large text (≥24px or ≥18.66px bold), and AAA. */
const LEVEL_THRESHOLD: Record<ContrastLevel, number> = {
  AA: 4.5,
  'AA-large': 3,
  AAA: 7,
};

/** Absorbs floating-point noise so a pair that computes to exactly 4.5 is not failed by 1e-16. */
const EPSILON = 1e-9;

/** True when `a` on `b` clears the given WCAG level. Defaults to AA (4.5:1). */
export function meetsContrast(a: string, b: string, level: ContrastLevel = 'AA'): boolean {
  const threshold = LEVEL_THRESHOLD[level] ?? LEVEL_THRESHOLD.AA;
  return contrastRatio(a, b) + EPSILON >= threshold;
}

/* ------------------------------------------------------------------------------------------------
 * Foregrounds and shades
 * ---------------------------------------------------------------------------------------------- */

/** Light foreground used when a brand ground is dark enough to carry it. */
const DEFAULT_LIGHT = '#ffffff';

/** Dark foreground used when a brand ground is pale — the design system's navy ink. */
const DEFAULT_DARK = '#0e1c33';

/** Overrides for the two candidate foregrounds. Unparseable values fall back to the defaults. */
export interface ReadableForegroundOptions {
  light?: string;
  dark?: string;
}

/**
 * Picks whichever of the light and dark foregrounds reads better on `background`. It always
 * returns the better of the two, even when neither reaches 4.5:1 — a caller that needs to know
 * whether the winner is actually good enough asks {@link meetsContrast}, or reads
 * `accentTextMeetsAA` off {@link brandPalette}. Ties go to the dark foreground.
 */
export function readableForeground(
  background: string,
  options?: ReadableForegroundOptions,
): string {
  const light = parseHexColor(options?.light ?? '') ? (options?.light as string) : DEFAULT_LIGHT;
  const dark = parseHexColor(options?.dark ?? '') ? (options?.dark as string) : DEFAULT_DARK;
  return contrastRatio(dark, background) >= contrastRatio(light, background) ? dark : light;
}

/**
 * Lightens (`amount > 0`) or darkens (`amount < 0`) a colour by a fraction of the distance to
 * white or black, for deriving hover and pressed states from a single brand colour. `amount` is
 * clamped to -1…1 — so `1`, and anything above it, is white, and `-1` and below is black — while
 * `0` or `NaN` returns the colour unchanged in normalized lower-case `#rrggbb` form. A colour that
 * cannot be parsed is returned untouched rather than silently becoming black.
 */
export function shade(hex: string, amount: number): string {
  const rgb = parseHexColor(hex);
  if (!rgb) return hex;
  const ratio = Number.isNaN(amount) ? 0 : Math.max(-1, Math.min(1, amount));
  if (ratio === 0) return toHex(rgb);
  const mix = (channel: number): number =>
    ratio > 0 ? channel + (255 - channel) * ratio : channel * (1 + ratio);
  return toHex({ r: mix(rgb.r), g: mix(rgb.g), b: mix(rgb.b) });
}

/* ------------------------------------------------------------------------------------------------
 * Brand palette
 * ---------------------------------------------------------------------------------------------- */

/** How far a hover state moves from its resting colour. */
const HOVER_SHADE = 0.14;

/**
 * Smallest contrast between a resting colour and its hover shade that still reads as a change.
 * Shading by a fraction of the remaining headroom barely moves a near-white or near-black colour,
 * so the gate catches the invisible hover as well as the unreadable one.
 */
const MIN_HOVER_SHIFT = 1.06;

/** Design-system defaults, mirrored from `DEFAULT_SETTINGS`, used when a stored colour is junk. */
const FALLBACK_PRIMARY = '#0e1c33';
const FALLBACK_ACCENT = '#f26a21';

/**
 * Derives a hover ground for `base`, given the text that sits on it.
 *
 * The shade moves *away* from the text colour: darkening a ground that already carries dark text
 * cuts the contrast, which is exactly how this project once shipped a 4.29:1 button. The derived
 * shade is then checked — for AA against the text, and for being visibly different from the
 * resting colour — and if it fails, the opposite direction is tried instead. When neither is both
 * safe and visible, the one that preserves the most contrast with the text wins.
 */
function hoverShade(base: string, text: string): string {
  const away = relativeLuminance(text) < relativeLuminance(base) ? HOVER_SHADE : -HOVER_SHADE;
  const first = shade(base, away);
  const second = shade(base, -away);
  const usable = (candidate: string): boolean =>
    meetsContrast(text, candidate, 'AA') && contrastRatio(base, candidate) >= MIN_HOVER_SHIFT;
  if (usable(first)) return first;
  if (usable(second)) return second;
  return contrastRatio(text, second) > contrastRatio(text, first) ? second : first;
}

/** Everything the apps need to set as `--alt-*` custom properties for one client's branding. */
export interface BrandPalette {
  primary: string;
  primaryHover: string;
  primaryText: string;
  accent: string;
  accentHover: string;
  accentText: string;
  /** False when no foreground clears 4.5:1 on the chosen accent — the admin app says so. */
  accentTextMeetsAA: boolean;
}

/**
 * Expands the two colours a client picks into the full set of values the stylesheets need, with
 * every foreground computed against its own ground rather than assumed. A colour that cannot be
 * parsed falls back to the design system's navy or orange, so a bad settings document degrades to
 * the stock brand instead of an unreadable one.
 */
export function brandPalette(primary: string, accent: string): BrandPalette {
  const primaryHex = parseHexColor(primary) ? shade(primary, 0) : FALLBACK_PRIMARY;
  const accentHex = parseHexColor(accent) ? shade(accent, 0) : FALLBACK_ACCENT;
  const primaryText = readableForeground(primaryHex);
  const accentText = readableForeground(accentHex);
  return {
    primary: primaryHex,
    primaryHover: hoverShade(primaryHex, primaryText),
    primaryText,
    accent: accentHex,
    accentHover: hoverShade(accentHex, accentText),
    accentText,
    accentTextMeetsAA: meetsContrast(accentText, accentHex, 'AA'),
  };
}
